'use client';

import { createPortal } from 'react-dom';
import { useEffect, useState, useRef, useCallback } from 'react';
import { useToastState, Severity, Toast, OVERFLOW_ID, COALESCE_ID } from '@/components/ToastContext';

// ── Severity style maps ───────────────────────────────────────────────────────

const SEV_CONTAINER: Record<Severity, string> = {
  error:   'bg-danger-bg border-danger-fg/30',
  warning: 'bg-warning-bg border-warning-fg/30',
  info:    'bg-info-bg border-info-fg/30',
  success: 'bg-success-bg border-success-fg/30',
};
const SEV_BAR: Record<Severity, string> = {
  error:   'bg-danger-fg',
  warning: 'bg-warning-fg',
  info:    'bg-info-fg',
  success: 'bg-success-fg',
};
const SEV_TEXT: Record<Severity, string> = {
  error:   'text-danger-fg',
  warning: 'text-warning-fg',
  info:    'text-info-fg',
  success: 'text-success-fg',
};

// ── Individual toast ──────────────────────────────────────────────────────────

function ToastItem({ toast, onDismiss, onRemove }: {
  toast: Toast;
  onDismiss: (id: string) => void;
  onRemove: (id: string) => void;
}) {
  return (
    <div
      role="alert"
      aria-live="assertive"
      className={`relative flex overflow-hidden rounded-[10px] border shadow-lg cursor-pointer select-none ${SEV_CONTAINER[toast.severity]}`}
      style={{
        width: 320,
        maxWidth: 'calc(100vw - 48px)',
        animation: toast.exiting
          ? 'toast-out 150ms ease-out forwards'
          : 'toast-in 180ms ease-out forwards',
      }}
      onClick={e => {
        if ((e.target as HTMLElement).closest('[data-toast-action]')) return;
        onDismiss(toast.id);
      }}
      onAnimationEnd={() => {
        if (toast.exiting) onRemove(toast.id);
      }}
    >
      <div className={`w-1 shrink-0 ${SEV_BAR[toast.severity]}`} />
      <div className="flex-1 px-3 py-2.5 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <p className={`text-sm font-semibold leading-snug ${SEV_TEXT[toast.severity]}`}>
            {toast.title}
            {toast.count > 1 && toast.id !== COALESCE_ID && (
              <span className="ml-1.5 text-xs font-normal opacity-70">×{toast.count}</span>
            )}
          </p>
          <button
            data-toast-action=""
            onClick={e => { e.stopPropagation(); onDismiss(toast.id); }}
            className={`shrink-0 text-xs leading-none transition-opacity hover:opacity-70 ${SEV_TEXT[toast.severity]}`}
            aria-label="Dismiss"
          >✕</button>
        </div>
        {toast.message && (
          <p className={`mt-0.5 text-xs leading-snug opacity-80 ${SEV_TEXT[toast.severity]}`}>
            {toast.message}
          </p>
        )}
        {toast.action && (
          <button
            data-toast-action=""
            onClick={e => { e.stopPropagation(); toast.action!.onClick(); }}
            className={`mt-1.5 text-xs font-medium underline transition-opacity hover:opacity-70 ${SEV_TEXT[toast.severity]}`}
          >{toast.action.label}</button>
        )}
      </div>
    </div>
  );
}

// ── Overflow chip (compact — represents N hidden sticky toasts) ───────────────

function OverflowChip({ toast, onDismiss, onRemove }: {
  toast: Toast;
  onDismiss: (id: string) => void;
  onRemove: (id: string) => void;
}) {
  return (
    <div
      role="status"
      className={`flex items-center justify-between gap-3 px-3 py-1.5 rounded-[8px] border shadow-md cursor-pointer select-none ${SEV_CONTAINER[toast.severity]}`}
      style={{
        width: 320,
        maxWidth: 'calc(100vw - 48px)',
        animation: toast.exiting
          ? 'toast-out 150ms ease-out forwards'
          : 'toast-in 180ms ease-out forwards',
      }}
      onClick={() => onDismiss(toast.id)}
      onAnimationEnd={() => { if (toast.exiting) onRemove(toast.id); }}
    >
      <span className={`text-xs font-semibold ${SEV_TEXT[toast.severity]}`}>{toast.title}</span>
      <span className={`text-xs opacity-55 ${SEV_TEXT[toast.severity]}`}>dismiss all ✕</span>
    </div>
  );
}

// ── Timer state ───────────────────────────────────────────────────────────────

type TimerEntry = {
  remaining: number;
  startedAt: number | null;
  handle: ReturnType<typeof setTimeout> | null;
  knownCreatedAt: number;
};

// ── Viewport (portal → document.body) ────────────────────────────────────────

export function ToastViewport() {
  const { toasts, dismiss, remove } = useToastState();
  const [mounted, setMounted] = useState(false);
  const timerStateRef = useRef<Map<string, TimerEntry>>(new Map());
  const hoveringRef = useRef(false);
  // Stable ref to dismiss so timer callbacks don't capture a stale version
  const dismissRef = useRef(dismiss);
  useEffect(() => { dismissRef.current = dismiss; }, [dismiss]);

  useEffect(() => { setMounted(true); }, []);

  // Manage auto-dismiss timers whenever the toast list changes
  useEffect(() => {
    const tsr = timerStateRef.current;

    // Clean up entries for toasts that are no longer in state
    const existingIds = new Set(toasts.map(t => t.id));
    for (const [id, s] of tsr) {
      if (!existingIds.has(id)) {
        if (s.handle) clearTimeout(s.handle);
        tsr.delete(id);
      }
    }

    // Add / reset timers for non-sticky, non-hidden, non-exiting toasts
    for (const t of toasts) {
      if (t.sticky || t.hidden || t.exiting) continue;

      const s = tsr.get(t.id);
      if (!s) {
        // New non-sticky toast — schedule immediately unless hovered
        const entry: TimerEntry = {
          remaining: t.duration, startedAt: null,
          handle: null, knownCreatedAt: t.createdAt,
        };
        tsr.set(t.id, entry);
        if (!hoveringRef.current) {
          entry.startedAt = Date.now();
          entry.handle = setTimeout(() => {
            tsr.delete(t.id);
            dismissRef.current(t.id);
          }, t.duration);
        }
      } else if (s.knownCreatedAt !== t.createdAt) {
        // Dedup bump reset — restart full duration
        if (s.handle) clearTimeout(s.handle);
        s.handle = null; s.startedAt = null;
        s.remaining = t.duration; s.knownCreatedAt = t.createdAt;
        if (!hoveringRef.current) {
          s.startedAt = Date.now();
          s.handle = setTimeout(() => {
            tsr.delete(t.id);
            dismissRef.current(t.id);
          }, t.duration);
        }
      }
    }
  }, [toasts]);

  // Pause all timers on hover; record remaining time
  const pauseTimers = useCallback(() => {
    hoveringRef.current = true;
    const now = Date.now();
    for (const [, s] of timerStateRef.current) {
      if (s.handle !== null) {
        clearTimeout(s.handle);
        s.handle = null;
        if (s.startedAt !== null) {
          s.remaining = Math.max(0, s.remaining - (now - s.startedAt));
          s.startedAt = null;
        }
      }
    }
  }, []);

  // Resume timers on mouse-leave with remaining duration
  const resumeTimers = useCallback(() => {
    hoveringRef.current = false;
    const now = Date.now();
    for (const [id, s] of timerStateRef.current) {
      if (s.handle === null && s.remaining > 0) {
        const capturedId = id;
        s.startedAt = now;
        s.handle = setTimeout(() => {
          timerStateRef.current.delete(capturedId);
          dismissRef.current(capturedId);
        }, s.remaining);
      }
    }
  }, []);

  if (!mounted) return null;

  // Render non-hidden toasts (including exiting ones — they animate out)
  // Sort oldest → newest so newest is nearest the corner (bottom)
  const visible = toasts
    .filter(t => !t.hidden)
    .sort((a, b) => a.createdAt - b.createdAt);

  return createPortal(
    <div
      aria-label="Notifications"
      className="fixed bottom-6 right-6 z-[200] flex flex-col gap-2 pointer-events-none"
      onMouseEnter={pauseTimers}
      onMouseLeave={resumeTimers}
    >
      {visible.map(t => (
        <div key={t.id} className="pointer-events-auto">
          {t.id === OVERFLOW_ID
            ? <OverflowChip toast={t} onDismiss={dismiss} onRemove={remove} />
            : <ToastItem    toast={t} onDismiss={dismiss} onRemove={remove} />
          }
        </div>
      ))}
    </div>,
    document.body,
  );
}
